import { Vector3 } from 'three'
import { object, number, array } from 'yup'

interface Point {
  x: number
  y: number
  z: number
}

export const DEFAULT_OFFSET = 1
export const DEFAULT_SPACING = 0.02
export const DEFAULT_MIN_POSITION_WIDTH = 0.25

export const stdGeneralizedPositionSchema = object({
  offset: number().required().min(0, 'Offset muss größer oder gleich 0 sein'),
  spacing: number().required().min(0, 'Abstand muss größer oder gleich 0 sein'),
  minPositionWidth: number().required().min(0, 'Mindestbreite muss größer oder gleich 0 sein'),
  shape: object({
    points: array(
      object({
        x: number().required().defined(),
        y: number().required().defined(),
        z: number().required().defined(),
      }),
    )
      .required()
      .test(
        'point-count',
        'Es werden 3 Punkte (Walmdach kurze Seite) oder 4 Punkte (Walmdach lange Seite) benötigt',
        points => points?.length === 3 || points?.length === 4,
      )
      .test(
        'same-z-coordinate-base',
        'Punkte 1 und 2 müssen dieselbe z-Koordinate haben',
        function (points) {
          if (!points || points.length !== 3) return true
          const [p1, p2] = points
          return (
            p1.z === p2.z ||
            this.createError({
              message: `Punkte 1 und 2 müssen dieselbe z-Koordinate haben. p1.z=${p1.z}, p2.z=${p2.z}`,
            })
          )
        },
      )
      .test(
        'higher-z-coordinate-apex',
        'Punkt 3 z-Koordinate muss größer als Punkt 1 z-Koordinate sein',
        function (points) {
          if (!points || points.length !== 3) return true
          const [p1, , p3] = points as Point[]
          return (
            p3!.z > p1!.z ||
            this.createError({
              message: `Punkt 3 z-Koordinate muss größer als Punkt 1 z-Koordinate sein. p3.z=${p3.z}, p1.z=${p1.z}`,
            })
          )
        },
      )
      .test(
        'trapezoid-shape',
        'Die Grundseite und obere Seite müssen horizontal sein: p1.z == p2.z und p3.z == p4.z',
        function (points) {
          if (!points || points.length !== 4) return true
          const [p1, p2, p3, p4] = points
          const baseZEqual = p1.z === p2.z
          const topZEqual = p3.z === p4.z
          return (
            (baseZEqual && topZEqual) ||
            this.createError({
              message: `Die Grundseite und obere Seite müssen horizontal sein: p1.z (${p1.z}) muss gleich p2.z (${p2.z}) und p3.z (${p3.z}) muss gleich p4.z (${p4.z}) sein`,
            })
          )
        },
      )
      .test(
        'base-length-greater-than-top',
        'Die Grundseite (p1,p2) muss kürzer oder gleich lang sein als die obere Seite (p3,p4). Die Reihenfolge der Punkte muss evtl. gedreht werden.',
        function (points) {
          if (!points || points.length !== 4) return true
          const [p1, p2, p3, p4] = points as Point[]

          const v1 = new Vector3(p1.x, p1.y, p1.z)
          const v2 = new Vector3(p2.x, p2.y, p2.z)
          const baseLength = v1.distanceTo(v2)

          const v3 = new Vector3(p3.x, p3.y, p3.z)
          const v4 = new Vector3(p4.x, p4.y, p4.z)
          const topLength = v3.distanceTo(v4)

          return (
            baseLength <= topLength ||
            this.createError({
              message: `Die Grundseite (${baseLength.toFixed(
                2,
              )}) muss kürzer oder gleich lang sein als die obere Seite (${topLength.toFixed(2)}). 
              Die Reihenfolge der Punkte muss evtl. gedreht werden.`,
            })
          )
        },
      )
      .test(
        'acute-angles',
        'Die Winkel 1 und 2 müssen spitz sein (weniger als 90 Grad)',
        function (points) {
          if (!points || points.length !== 4) return true
          const [p1, p2, p3, p4] = points as Point[]

          const v1 = new Vector3(p1.x, p1.y, p1.z)
          const v2 = new Vector3(p2.x, p2.y, p2.z)
          const v3 = new Vector3(p3.x, p3.y, p3.z)
          const v4 = new Vector3(p4.x, p4.y, p4.z)

          const v1_2 = v2.clone().sub(v1)
          const v1_3 = v3.clone().sub(v1)
          const angle1 = v1_2.angleTo(v1_3) * (180 / Math.PI)

          const v2_1 = v1.clone().sub(v2)
          const v2_4 = v4.clone().sub(v2)
          const angle2 = v2_1.angleTo(v2_4) * (180 / Math.PI)

          const anglesAcute = angle1 < 90 && angle2 < 90

          return (
            anglesAcute ||
            this.createError({
              message: `Die Winkel 1 und 2 müssen spitz sein. Got angle1=${angle1.toFixed(
                1,
              )}°, angle2=${angle2.toFixed(1)}°`,
            })
          )
        },
      )
      .test(
        'cartesian-base-side',
        'Die Grundseite muss kartesisch sein (gleicher x oder y Wert)',
        function (points) {
          if (!points || points.length < 2) return true
          const [p1, p2] = points
          const isXEqual = p1.x === p2.x
          const isYEqual = p1.y === p2.y
          return (
            isXEqual ||
            isYEqual ||
            this.createError({
              message: `Die Grundseite muss kartesisch sein. Punkt 1 und 2 müssen entweder denselben x-Wert (p1.x=${p1.x}, p2.x=${p2.x}) oder denselben y-Wert (p1.y=${p1.y}, p2.y=${p2.y}) haben`,
            })
          )
        },
      ),
  }).required(),
})
